import { ASSET_KINDS, type AssetKind } from "@pax/api-contract/assets/v1alpha1";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { LibraryScreen } from "@/features/assets/components/LibraryScreen";
import { type LibraryFilters, normalizeAssetQuery } from "@/features/assets/queries";

const SEARCH_DEBOUNCE_MS = 300;
const TAG = /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const on = (value: unknown) => (value === true || value === "true" ? true : undefined);

export const Route = createFileRoute("/assets/")({
  validateSearch: (search: Record<string, unknown>): LibraryFilters => {
    const kind = ASSET_KINDS.includes(search.kind as AssetKind)
      ? (search.kind as AssetKind)
      : undefined;
    const sort = search.sort === "mostUsed" || search.sort === "topRated" ? search.sort : undefined;
    const tag = typeof search.tag === "string" && TAG.test(search.tag) ? search.tag : undefined;
    const collection =
      typeof search.collection === "string" && UUID.test(search.collection)
        ? search.collection
        : undefined;
    const q = normalizeAssetQuery(search.q);
    return {
      ...(q && { q }),
      ...(kind && { kind }),
      ...(sort && { sort }),
      ...(tag && { tag }),
      ...(collection && { collection }),
      ...(on(search.hideSensitive) && { hideSensitive: true }),
      ...(on(search.humanVerifiedOnly) && { humanVerifiedOnly: true }),
      ...(on(search.excludeAiGenerated) && { excludeAiGenerated: true }),
    };
  },
  head: () => ({ meta: [{ title: "Assets · Pax.co" }] }),
  component: LibraryPage,
});

function LibraryPage() {
  const filters = Route.useSearch();
  const navigate = Route.useNavigate();
  const [searchValue, setSearchValue] = useState(filters.q ?? "");

  useEffect(() => {
    setSearchValue(filters.q ?? "");
  }, [filters.q]);

  // The field is typed into faster than search can answer, so the address —
  // which is what asks — follows a few hundred milliseconds behind it.
  useEffect(() => {
    const next = normalizeAssetQuery(searchValue);
    if (next === filters.q) return;
    const timer = window.setTimeout(() => {
      void navigate({
        search: previous => ({ ...previous, q: next }),
        replace: true,
        resetScroll: false,
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [navigate, filters.q, searchValue]);

  const change = useCallback(
    (next: Partial<LibraryFilters>) =>
      void navigate({ search: previous => ({ ...previous, ...next }), replace: true }),
    [navigate],
  );

  return (
    <LibraryScreen
      filters={filters}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      onChange={change}
    />
  );
}
